<template>
  <div class="opc-foldable" :class="{'is-active': value}">
    <zm-collapse ref="collapse" accordion :value="val">
      <zm-collapse-item :name="collapseName">
        <div slot="title" @click.stop="onToggle" @keydown.enter.stop="onToggle" :aria-hidden="titleAriaHidden ? 'true' : 'false'">
          <div v-if="$slots.head" class="opc-foldable__head" @click.stop @keydown.enter.stop @keydown.space.stop>
            <slot name="head" />
          </div>
          <div v-if="$slots.trigger" class="opc-foldable__trigger" @click.stop="onToggle" @keydown.enter.stop="onToggle">
            <slot name="trigger" />
          </div>
          <span class="opc-foldable__action" :tabindex="arrow?'0':'-1'" role="button" :aria-label="iconAriaLabel" :aria-expanded="String(value)">
            <span v-if="$slots.action" @click.stop>
              <slot name="action" />
            </span>
            <span
              v-else
              tabindex="-1"
              role="button"
              :aria-label="editAriaLabel"
              :aria-expanded="String(value)"
              @click.stop="onToggle"
              v-show="edit"
              class="opc-foldable__action__edit"
            >
              {{ this.editLabel || $t('billing.common.edit') }}
            </span>
            <span tabindex="-1" v-if="arrow" @click.stop="onToggle" @keydown.enter.stop="onToggle" class="opc-foldable__action__icon"
              role="button"
              :aria-label="iconAriaLabel"
              :aria-expanded="String(value)"
            >
              <i class="zm-icon-up"/>
            </span>
          </span>
        </div>
        <slot />
      </zm-collapse-item>
    </zm-collapse>
    <div v-if="$slots.collapse && !value">
      <slot name="collapse" />
    </div>
  </div>
</template>

<script>
/*
 * $slots.action: triggers the collapse status
 * $slots.head: the fixed view part beyond collapse
 * $slots.action: actions that will override the default edit button. normally we won't use it
 */
export default {
  props: {
    arrow: {
      // show top-right drop down arrow
      type: Boolean,
      default: true,
    },
    edit: {
      // show default edit button, will be overwriten by $slots.action
      type: Boolean,
      default: true,
    },
    editLabel: {
      // default edit button label
      type: String,
    },
    value: {
      // used in v-model
      type: Boolean,
      default: false,
    },
    editAriaLabel: {
      type: String,
      default: 'Edit',
    },
    title: {
      type: String,
      default: '',
    },
    isRemoveWrapAriaLabelledby: {
      type: Boolean,
      default: false,
    },
    titleAriaHidden: {
      type: Boolean,
      default: false,
    }
  },
  data() {
    return {
      collapseName: 'x',
    };
  },
  model: {
    prop: 'value',
    event: 'change',
  },
  computed: {
    val() {
      return [this.value ? this.collapseName : ''];
    },
    iconAriaLabel() {
      return this.title ? `${(this.value ? '' : 'Edit ')}${this.title} plan` : this.editAriaLabel;
    },
  },
  methods: {
    onToggle() {
      this.$emit('change', !this.value);
      if (this.$context?.is.zs2ze) {
        this.value === false && this.title && this.$tracking.buyInteraction('product details accordion', `zoom events - edit`);
      } else {
        this.value === false && this.title && this.$tracking.buyInteraction(this.title, `${this.title} -edit`);
      }
    },
    reduceCollapseAx(){
      const headerDom = this.$el.querySelector('.zm-collapse-item__header');
      headerDom?.removeAttribute('tabindex');
      headerDom?.removeAttribute('role');
      this.$refs.collapse.$el.removeAttribute('role');
    },
    // In windows, it would cause NVDA read redundant info "[plan names].property page" [ZOOM-553124]
    removeRedundantAxMessage(){
      const headerDom = this.$el.querySelector('.zm-collapse-item__wrap');
      headerDom?.removeAttribute('aria-labelledby');
    },
  },
  mounted() {
    this.reduceCollapseAx();
    if(this.isRemoveWrapAriaLabelledby) {
      this.removeRedundantAxMessage();
    }
  },
};
</script>

<style lang="scss">
@import '../styles/_variables.scss';
@import '@zoom/billing-responsive/styles/responsive.scss';
@import '@/styles/_variables.scss';

.opc-foldable {
  $theme-color: $theme-color;
  $gap: 9px;
  $head-height: 28px;
  &__trigger {
  }
  &__action {
    cursor: pointer;
    position: absolute;
    top: 0;
    right: 0;
    z-index: 1;
    padding-right: $gap;
    font-weight: 700;
    font-size: 14px;
    line-height: $head-height;
    color: $theme-color;
    user-select: none;
    padding-left: 4px;
    background: rgba(255, 255, 255, 0.8);
    border-radius: 6px;

    height: fit-content;
    span {
      margin-left: 16px;
    }
    &:focus-visible {
      outline: none;
      box-shadow: 0 0 0 2px #fff, 0 0 0 4px $focus-color;
      border-radius: 1px;
    }
    &__edit {
      transition: all 0.3s ease;
      color: $theme-color;
      &:hover {
        color: #274d83;
      }
    }
    &__icon {
      display: inline-block;
      margin-left: $gap;
      transition: all 0.3s ease;
      position: relative;
      top: 1px;
      transform: rotate(180deg);
      i {
        transition: all 0.3s ease;
        &:hover {
          transform: scale(1.4);
        }
      }
    }

    @include media(mobile) {
      font-size: 14px;
      padding-right: $gap;
      background: transparent;
      span {
        margin-left: 16px;
      }
    }
  }
  &__head {
    position: relative;
    cursor: default;
    min-height: $head-height;
  }
  .zm-collapse {
    border-top: none;
    border-bottom: none;
    &-item {
      position: relative;
      &__header {
        padding-top: 0px;
        padding-bottom: 0px;
        border-bottom: none !important;
        min-height: $head-height;
      }
      &__wrap {
        border-bottom: none;
        overflow: visible;
      }
      &__content {
        padding-bottom: 0;
      }
      &__arrow {
        display: none;
      }

      .opc-foldable__action__icon {
        padding: 0px;
        margin-left: 4px;
      }

      &.is-active {
        .opc-foldable__action__edit {
          display: none;
        }
        .opc-foldable__action__icon {
          transform: rotate(0deg);
        }
      }
    }
  }
  &__heading{
    font-size: 18px;
    font-style: normal;
    font-weight: 700;
    line-height: 35px;
    color: #00031F;
  }
  &__text{
    font-size: 14px;
    font-style: normal;
    font-weight: 500;
    line-height: 18px;
    color: #666487;
  }
  &__space{
    margin-bottom: 12px;
  }
  &__space2x{
    margin-bottom: 24px;
  }
}
</style>
